import { Content } from "pdfmake/interfaces";
import { FUTURE_HAZARD_RANGE_LABELS } from "helpers/future-hazard-legend";
import svg from "report/common/svg";
import getTable from "report/crs/table/table";
import {
  ClimateRiskReportCategory,
  isColorlessClimateRiskCategory
} from "../types";
import { calculateMergeRules } from "./calculate";
import { getFutureHazardRiskLevel, getRiskLevel } from "./color";
import { combineScenarioArrays } from "./combine";
import { convertNumToRiskLevel } from "./convert";
import { formatTableRows } from "./format";
import generateRiskLevelLegend from "./legend";
import { setColumnAlignment } from "./alignment";
import { applyMergeRulesToColumns } from "./batch";

const get = (
  t: any,
  sSP126Arr: any[],
  sSP245Arr: any[],
  sSP370Arr: any[],
  sSP585Arr: any[],
  category: ClimateRiskReportCategory = "climate"
): Content[] => {
  const isColorlessCategory = isColorlessClimateRiskCategory(category);
  const legend1 = [
    t("report.Negligible (0 - 2)"),
    t("report.Minor (2 - 4)"),
    t("report.Moderate (4 - 6)"),
    t("report.Significant (6 - 8)"),
    t("report.Extreme (8 - 10)")
  ];

  const legend2 = [
    t("report.Negligible"),
    t("report.Minor"),
    t("report.Moderate"),
    t("report.Significant"),
    t("report.Extreme")
  ];

  // futureHazard 的风险等级标签（与前端页面一致：6级）
  const futureHazardLegend2 = [
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[1] ||
      "Very Low",
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[2] || "Low",
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[3] ||
      "Moderate",
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[4] || "High",
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[5] ||
      "Very High",
    t("legend.futureHazardData", { returnObjects: true })?.[0]?.[6] || "Extreme"
  ];

  // futureHazard 的 PDF 分数范围标签与页面图例分离，优先走 i18n，常量仅作兜底
  const futureHazardLegend1 =
    (t("report.futureHazardLegend1Labels", {
      returnObjects: true
    }) as string[]) || FUTURE_HAZARD_RANGE_LABELS;

  // 复制数组避免修改原始数据
  const ssp126 = JSON.parse(JSON.stringify(sSP126Arr));
  const ssp245 = JSON.parse(JSON.stringify(sSP245Arr));
  const ssp370 = JSON.parse(JSON.stringify(sSP370Arr));
  const ssp585 = JSON.parse(JSON.stringify(sSP585Arr));

  // 在每个数组的第一列后面插入气候情景标识
  ssp126.forEach((row: string[]) => row.splice(1, 0, "SSP1-2.6"));
  ssp245.forEach((row: string[]) => row.splice(1, 0, "SSP2-4.5"));
  ssp370.forEach((row: string[]) => row.splice(1, 0, "SSP3-7.0"));
  ssp585.forEach((row: string[]) => row.splice(1, 0, "SSP5-8.5"));

  // 交错合并数据
  const combinedArray = combineScenarioArrays(ssp126, ssp245, ssp370, ssp585);

  // 使用 formatTableRows 处理这个组合后的数组
  const row = formatTableRows(combinedArray, svg, t, category);

  // 基于第1列计算规则
  const mergeRules = calculateMergeRules(row, 1);

  // 合并第0列和1列
  const mergedRow = applyMergeRulesToColumns(row, [0, 1], mergeRules);

  // 将第3列设置为左对齐
  const leftAlignedRow = setColumnAlignment(mergedRow, 2, "left", false);

  // 转换字符描述
  const riskLevelRow = convertNumToRiskLevel(
    t,
    leftAlignedRow,
    category === "futureHazard" ? getFutureHazardRiskLevel : getRiskLevel
  );

  if (isColorlessCategory) {
    return [
      ...getTable(
        t("report.table5"),
        t("report.titleVariables"),
        leftAlignedRow
      )
    ];
  }

  const isFutureHazard = category === "futureHazard";
  const tableAKey = isFutureHazard ? "report.table3" : "report.table1";
  const titleAKey = isFutureHazard ? "report.title3" : "report.title1";
  const tableBKey = isFutureHazard ? "report.table4" : "report.table2";
  const titleBKey = isFutureHazard ? "report.title4" : "report.title2";

  return [
    ...getTable(t(tableAKey), t(titleAKey), leftAlignedRow),
    ...generateRiskLevelLegend(
      isFutureHazard ? t("report.futureHazardLegend1") : t("report.legend1"),
      isFutureHazard ? futureHazardLegend1 : legend1,
      isFutureHazard
    ),
    ...getTable(t(tableBKey), t(titleBKey), riskLevelRow),
    ...generateRiskLevelLegend(
      t("report.legend2"),
      isFutureHazard ? futureHazardLegend2 : legend2,
      isFutureHazard
    )
  ];
};

export default get;
